import { useDeferredValue, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '@/hooks/useCurrency';
import { Barcode, Eye, Package, PackagePlus, ScanLine, Tags } from 'lucide-react';
import {
  LensInclusion,
  PERMISSIONS,
  ProductStatus,
  ProductType,
  type ProductStatus as ProductStatusType,
  type ProductSummary,
  type ProductType as ProductTypeType,
} from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { PageHeader } from '@/components/ui/PageHeader';
import { Pagination } from '@/components/ui/Pagination';
import { Select } from '@/components/ui/Select';
import { useLocale } from '@/hooks/useLocale';
import { usePermissions } from '@/hooks/usePermissions';
import { apiErrorMessage } from '@/services/apiClient';
import { formatDate } from '@/utils/format';
import { useCategoriesQuery } from '@/modules/inventory/api/inventory.api';
import { useProductsQuery } from '../api/catalog.api';
import { categoryLabel, localizedName } from '../category-label';
import { ProductFormDrawer } from '../components/ProductFormDrawer';
import { ProductSearch } from '../components/ProductSearch';
import { ProductStatusBadge } from '../components/ProductStatusBadge';
import { ProductThumb } from '../components/ProductThumb';
import { ProductTypeBadge } from '../components/ProductTypeBadge';

const PAGE_SIZE = 20;

export function ProductsPage() {
  const { t, i18n } = useTranslation();
  const { format: formatMoney } = useCurrency();
  const { locale } = useLocale();
  const { can } = usePermissions();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [type, setType] = useState<ProductTypeType | ''>('');
  const [status, setStatus] = useState<ProductStatusType | ''>('');
  const [categoryId, setCategoryId] = useState('');
  const [lensInclusion, setLensInclusion] = useState<LensInclusion | ''>('');
  const [page, setPage] = useState(1);
  const [isCreateOpen, setCreateOpen] = useState(false);
  const [isScanOpen, setScanOpen] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  const deferredSearch = useDeferredValue(search);

  const { data, isPending, isError, error, refetch, isFetching } = useProductsQuery({
    page,
    pageSize: PAGE_SIZE,
    search: deferredSearch,
    type,
    status,
    categoryId,
    lensInclusion,
  });
  const categoriesQuery = useCategoriesQuery();

  const canCreate = can(PERMISSIONS.CATALOG_CREATE);
  const resetToFirstPage = () => setPage(1);

  const typeOptions = [
    { value: '', label: t('catalog.filters.allTypes') },
    ...Object.values(ProductType).map((value) => ({ value, label: t(`catalog.types.${value}`) })),
  ];

  const categoryOptions = [
    { value: '', label: t('catalog.filters.allCategories') },
    ...(categoriesQuery.data?.items ?? []).map((category) => ({
      value: category.id,
      label: categoryLabel(category, i18n.language),
    })),
  ];

  const lensInclusionOptions = [
    { value: '', label: t('catalog.filters.anyLensInclusion') },
    ...Object.values(LensInclusion).map((value) => ({
      value,
      label: t(`catalog.lensInclusion.${value}`),
    })),
  ];

  const statusOptions = [
    { value: '', label: t('common.all') },
    ...Object.values(ProductStatus).map((value) => ({
      value,
      label: t(`status.${value.toLowerCase()}`),
    })),
  ];

  const columns: Column<ProductSummary>[] = [
    {
      key: 'product',
      header: t('catalog.fields.name'),
      primary: true,
      render: (product) => (
        <span className="flex items-center gap-3">
          <ProductThumb image={product.image} alt={product.name} />
          <span className="flex min-w-0 flex-col">
            <span className="text-ink font-medium">{product.name}</span>
            <span className="text-muted font-mono text-xs" dir="ltr">
              {product.productNumber}
            </span>
          </span>
        </span>
      ),
    },
    {
      key: 'type',
      header: t('catalog.fields.type'),
      render: (product) => <ProductTypeBadge type={product.type} />,
    },
    {
      key: 'brand',
      header: t('catalog.fields.brand'),
      render: (product) => <span className="text-muted">{product.brandName ?? '—'}</span>,
    },
    {
      key: 'category',
      header: t('catalog.fields.category'),
      render: (product) => (
        <span className="text-muted" dir="auto">
          {product.categoryName
            ? localizedName(product.categoryName, product.categoryNameAr, i18n.language)
            : '—'}
        </span>
      ),
    },
    {
      key: 'variants',
      header: t('catalog.fields.variants'),
      align: 'end',
      render: (product) => (
        <span className="text-muted" dir="ltr">
          {product.variantCount}
        </span>
      ),
    },
    {
      key: 'price',
      header: t('catalog.fields.fromPrice'),
      align: 'end',
      render: (product) => (
        <span className="text-ink font-medium">{formatMoney(product.fromPriceMinor)}</span>
      ),
    },
    {
      key: 'status',
      header: t('catalog.fields.status'),
      render: (product) => <ProductStatusBadge status={product.status} />,
    },
    {
      key: 'updatedAt',
      header: t('catalog.fields.lastUpdated'),
      render: (product) => (
        <span className="text-muted text-xs">{formatDate(product.updatedAt, locale)}</span>
      ),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      align: 'end',
      hideOnCard: true,
      render: (product) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => void navigate(`/inventory/products/${product.id}`)}
          aria-label={t('catalog.products.viewNamed', { name: product.name })}
        >
          <Eye className="size-4" aria-hidden />
          <span className="hidden lg:inline">{t('catalog.products.view')}</span>
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('catalog.products.title')}
        subtitle={t('catalog.products.subtitle')}
        actions={
          <>
            <Button variant="secondary" onClick={() => void navigate('/inventory/categories')}>
              <Tags className="size-4" aria-hidden />
              {t('catalog.categories.title')}
            </Button>

            <Button variant="secondary" onClick={() => void navigate('/inventory/labels')}>
              <Barcode className="size-4" aria-hidden />
              {t('catalog.labels.printLabels')}
            </Button>

            <Button variant="secondary" onClick={() => setScanOpen(true)}>
              <ScanLine className="size-4" aria-hidden />
              {t('catalog.search.scan')}
            </Button>

            {canCreate ? (
              <Button onClick={() => setCreateOpen(true)}>
                <PackagePlus className="size-4" aria-hidden />
                {t('catalog.products.create')}
              </Button>
            ) : null}
          </>
        }
      />

      {feedback ? <Alert tone="success">{feedback}</Alert> : null}

      <Card>
        <CardHeader
          title={t('catalog.products.title')}
          subtitle={
            data ? t('catalog.products.count', { count: data.pagination.totalItems }) : undefined
          }
          action={
            <div className="flex flex-wrap items-center gap-2">
              <div className="w-40 sm:w-52">
                <Input
                  type="search"
                  value={search}
                  onChange={(event) => {
                    setSearch(event.target.value);
                    resetToFirstPage();
                  }}
                  placeholder={t('catalog.search.placeholder')}
                  aria-label={t('catalog.search.label')}
                />
              </div>

              <div className="w-36">
                <Select
                  options={typeOptions}
                  value={type}
                  aria-label={t('catalog.fields.type')}
                  onChange={(event) => {
                    setType(event.target.value as ProductTypeType | '');
                    resetToFirstPage();
                  }}
                />
              </div>

              <div className="w-40">
                <Select
                  options={categoryOptions}
                  value={categoryId}
                  aria-label={t('catalog.fields.category')}
                  onChange={(event) => {
                    setCategoryId(event.target.value);
                    resetToFirstPage();
                  }}
                />
              </div>

              <div className="w-36">
                <Select
                  options={lensInclusionOptions}
                  value={lensInclusion}
                  aria-label={t('catalog.fields.lensInclusion')}
                  onChange={(event) => {
                    setLensInclusion(event.target.value as LensInclusion | '');
                    resetToFirstPage();
                  }}
                />
              </div>

              <div className="w-28">
                <Select
                  options={statusOptions}
                  value={status}
                  aria-label={t('catalog.fields.status')}
                  onChange={(event) => {
                    setStatus(event.target.value as ProductStatusType | '');
                    resetToFirstPage();
                  }}
                />
              </div>
            </div>
          }
        />

        <DataTable
          caption={t('catalog.products.tableCaption')}
          columns={columns}
          rows={data?.items}
          rowKey={(product) => product.id}
          cardActions={(product) => (
            <Button
              variant="secondary"
              size="sm"
              className="w-full justify-center"
              onClick={() => void navigate(`/inventory/products/${product.id}`)}
              aria-label={t('catalog.products.viewNamed', { name: product.name })}
            >
              <Eye className="size-4" aria-hidden />
              {t('catalog.products.view')}
            </Button>
          )}
          isLoading={isPending}
          isError={isError}
          errorMessage={apiErrorMessage(error) ?? t('catalog.products.loadError')}
          onRetry={() => void refetch()}
          emptyIcon={Package}
          emptyTitle={
            deferredSearch || type || status || categoryId || lensInclusion
              ? t('catalog.products.noMatches')
              : t('catalog.products.empty')
          }
          emptyDescription={
            deferredSearch || type || status || categoryId || lensInclusion
              ? t('catalog.products.noMatchesHint')
              : t('catalog.products.emptyHint')
          }
        />

        {data ? (
          <Pagination pagination={data.pagination} onPageChange={setPage} isBusy={isFetching} />
        ) : null}
      </Card>

      {canCreate ? (
        <ProductFormDrawer
          isOpen={isCreateOpen}
          onClose={() => setCreateOpen(false)}
          onSaved={(product, message) => {
            setFeedback(message);
            void navigate(`/inventory/products/${product.id}`);
          }}
        />
      ) : null}

      <Modal
        isOpen={isScanOpen}
        onClose={() => setScanOpen(false)}
        title={t('catalog.search.scanTitle')}
        description={t('catalog.search.scanSubtitle')}
      >
        <ProductSearch
          autoFocus
          onSelectProduct={(product) => {
            setScanOpen(false);
            void navigate(`/inventory/products/${product.id}`);
          }}
          onSelectVariant={(_variant, product) => {
            setScanOpen(false);
            void navigate(`/inventory/products/${product.id}`);
          }}
        />
      </Modal>
    </div>
  );
}
